import { Asset } from '@/api/generatedApi';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { GET_ASSET_QUERY_KEY } from './useGetAsset';
import { LIST_ASSETS_BY_STATE_QUERY_KEY } from './useFetchAssetsByState';
import { ASSET_STATE_COUNTS_QUERY_KEY } from './useGetAssetStateCounts';
import { SEARCH_RESULT_QUERY_KEYS } from '../search/searchQueryKeys';

export const useReplaceAssetFile = ({
  asset,
  onSuccess,
  onError,
}: {
  asset?: Asset;
  onSuccess?: (asset: Asset) => void;
  onError?: (message: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationKey: ['replaceAssetFile', asset?.id],
    mutationFn: async (file: File) => {
      const response = await api.assets.replaceAssetFile(tenantId, asset?.id ?? '', { file });
      const replaced = response.data;
      queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY, { assetId: replaced.id, tenantId }] });
      for (const queryKey of SEARCH_RESULT_QUERY_KEYS) {
        queryClient.resetQueries({ queryKey: [queryKey], exact: false });
      }
      return replaced;
    },
    onSuccess: replaced => {
      onSuccess?.(replaced);
    },
    onError: (error_: AxiosError<{ message: string }>) => {
      onError?.(error_.response?.data?.message ?? 'Failed to replace asset file');
    },
  });

  return {
    replaceAssetFile: mutate,
    replaceAssetFileAsync: mutateAsync,
    isPending,
  };
};
